import React, { useContext, useState } from "react"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  TextField,
  Typography,
} from "@mui/material"
import PropTypes from "prop-types"
import AuthContext from "context/auth/authContext"
import { getCurrency } from "utils/functions"
import i18n from "i18n"

const clampPercent = value => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return 0
  return Math.min(Math.max(numeric, 0), 100)
}

const InvoicingToolbar = ({
  isInvoicing,
  onToggle,
  onClearSelection,
  onCreateInvoice,
  onApplyPercent,
  selectedPartsCount,
  totalInvoicing,
  disabled = false,
}) => {
  const { user } = useContext(AuthContext)
  const currencySymbol = getCurrency(user?.["custom:currency"])

  const [percentInput, setPercentInput] = useState("")

  const handlePercentChange = event => {
    const { value } = event.target

    if (value === "") {
      setPercentInput("")
      return
    }

    const numeric = Number(value)
    if (Number.isNaN(numeric)) return

    if (numeric > 100) {
      setPercentInput("100")
      return
    }

    if (numeric < 0) {
      setPercentInput("0")
      return
    }

    setPercentInput(value)
  }

  const handleApply = () => {
    if (percentInput === "") return
    if (selectedPartsCount === 0) return
    onApplyPercent(clampPercent(percentInput))
  }

  const handlePercentKeyDown = event => {
    if (event.key === "Enter") {
      event.preventDefault()
      handleApply()
    }
  }

  const isApplyDisabled =
    selectedPartsCount === 0 ||
    percentInput === "" ||
    Number.isNaN(Number(percentInput))

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}
    >
      <ButtonBase
        onClick={disabled ? undefined : onToggle}
        disabled={disabled}
        aria-expanded={isInvoicing}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 0.5,
          py: 0.25,
          borderRadius: 1,
          whiteSpace: "nowrap",
          color: "inherit",
          opacity: disabled ? 0.38 : 1,
          "&:hover": {
            backgroundColor: disabled ? "transparent" : "action.hover",
          },
        }}
      >
        <Typography variant="body1" component="span">
          {i18n.t("certHistory.invoiceDraft.toggle")}
        </Typography>
        <ChevronRightIcon
          sx={{
            fontSize: 20,
            transition: theme =>
              theme.transitions.create("transform", {
                duration: theme.transitions.duration.shortest,
              }),
            transform: isInvoicing ? "rotate(90deg)" : "rotate(0deg)",
          }}
        />
      </ButtonBase>

      <Collapse in={isInvoicing && !disabled} timeout="auto" unmountOnExit>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
            mt: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" component="span">
            {i18n.t("certHistory.invoiceDraft.completePercentage")}
          </Typography>

          <TextField
            type="number"
            size="small"
            value={percentInput}
            onChange={handlePercentChange}
            onKeyDown={handlePercentKeyDown}
            inputProps={{ min: 0, max: 100, inputMode: "numeric" }}
            sx={{ width: 140 }}
          />
          <Button
            type="button"
            variant="outlined"
            onClick={handleApply}
            disabled={isApplyDisabled}
          >
            {i18n.t("certHistory.invoiceDraft.apply")}
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={onClearSelection}
          >
            {i18n.t("certHistory.invoiceDraft.clear")}
          </Button>
          <Typography variant="body2" component="span">
            {i18n.t("certHistory.invoiceDraft.totalToInvoice")}{" "}
            {totalInvoicing.toFixed(2)} {currencySymbol}
          </Typography>
          <Button
            type="button"
            variant="contained"
            color="branded"
            onClick={onCreateInvoice}
            disabled={totalInvoicing === 0}
          >
            {i18n.t("certHistory.invoiceDraft.createInvoice")}
          </Button>
        </Box>
      </Collapse>
    </Box>
  )
}

InvoicingToolbar.propTypes = {
  isInvoicing: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onClearSelection: PropTypes.func.isRequired,
  onCreateInvoice: PropTypes.func.isRequired,
  onApplyPercent: PropTypes.func.isRequired,
  selectedPartsCount: PropTypes.number.isRequired,
  totalInvoicing: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
}

export default InvoicingToolbar
